import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * useSyncStatus
 *
 * Fetches GET /api/sync/status and returns { sources, recentLogs, refetch }.
 *   sources    : [{ type, status, lastSync }]
 *   recentLogs : up to 20 sync log entries, newest first
 *   refetch()  : re-pulls immediately; called after any action that changes
 *                sync state (sync button click, insight generation, demo seed)
 *
 * Unlike useDashboardData, this doesn't auto-retry on 401 beyond calling
 * logout — sync status isn't useful without an authenticated session.
 */
export function useSyncStatus() {
  const { logout, authenticated, client: homeClient } = useAuth();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId");
  const url = clientId ? `/api/sync/status?clientId=${clientId}` : "/api/sync/status";
  // Staff sessions have no home client — without a ?clientId selection there
  // is no tenant to ask about, and the API would 400. Skip the fetch.
  const hasClientScope = Boolean(clientId || homeClient);
  const [state, setState] = useState({
    sources: null,
    recentLogs: null,
    // Whether this client is the one PRISM_DEMO_CLIENT_SLUG sanctions. Defaults
    // to false and stays false on any error: the button it gates must never
    // appear because a fetch failed to say it shouldn't.
    demoSeedingAllowed: false,
    loading: true,
    error: null,
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!authenticated) return;
    if (!hasClientScope) {
      setState({ sources: [], recentLogs: [], demoSeedingAllowed: false, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    apiFetch(url)
      .then((data) => {
        if (cancelled) return;
        setState({
          sources: data.sources || [],
          recentLogs: data.recentLogs || [],
          demoSeedingAllowed: data.demoSeedingAllowed === true,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          logout();
          return;
        }
        setState((s) => ({ ...s, loading: false, error: err }));
      });

    return () => {
      cancelled = true;
    };
  }, [authenticated, hasClientScope, logout, nonce, url]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, refetch };
}
